import { Grid, Typography } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import _ from "lodash";
import { observer } from "mobx-react";
import moment from "moment";
import React, { FunctionComponent, useEffect, useState } from "react";
import { sanitizeHtml } from "utils/sanitizeHtml";
import { Answer, AnswerAlert } from "../../../../models/Answer";
import {
    AnswerTypeNames,
    AnswerTypeVariations,
} from "../../../../models/AnswerType";
import Question, { QuestionType } from "../../../../models/Question";
import { Tag, TagSentiment } from "../../../../models/Tag";
import theme from "../../../../Theme/AppTheme";
import { useStore } from "../../../../utils/useStore";
import AcxMainTextField from "../../../UI/AcxMainTextField";
import AcxCalendarPopup from "../../../UI/Calendar/AcxCalendarPopup";
import AcxSelectMulti from "../../../UI/Select/BaseSelectComponents/AcxSelectMulti";
import AcxSelectSingle from "../../../UI/Select/BaseSelectComponents/AcxSelectSingle";
import { ModuleUIModel } from "../../Models/ModuleUIModel";
import { EvalStore } from "../../Stores/EvalStore";
import AIExplanationCards from "./AIExplanations/AIExplanationsCards";
import SmartPredictToggle from "./AIExplanations/SmartPredictToggle";
import QuoteNote from "./QuoteNote";
import QuoteNoteButtonContainer from "./QuoteNoteButtonContainer";
import AcxQuestionDescription from "components/UI/AcxQuestionDescription";
import AcxQuestionRubric from "components/UI/AcxQuestionRubric";

interface OwnProps {
    question: Question;
    moduleUIModel?: ModuleUIModel;
    standalone?: boolean;
    leftBorderColor?: string;
    isDisabled?: boolean;
}

type Props = OwnProps;

const selectCustomStyle = {
    menuPortal: (provided, state) => ({ ...provided, zIndex: 9999 }),
};

const TagOrFillInResponseAnswer: FunctionComponent<Props> = observer(
    (props) => {
        const store = useStore(EvalStore);
        const answer: Answer | undefined =
            store.currentEval?.getAnswerForQuestion(
                props.question,
                props.moduleUIModel?.licensedModule.evaluationModuleId,
            );

        let activeTags: Tag | Tag[] | undefined = answer?.activeTags;
        const firstActiveTag =
            (answer?.activeTags.length ?? 0) > 0
                ? answer?.activeTags[0]
                : undefined;

        if (
            props.question.answerType.variation === AnswerTypeVariations.Single
        ) {
            activeTags =
                (activeTags?.length ?? 0) > 0 ? activeTags?.[0] : undefined;
        }

        const isSmartPredicted = answer?.hasAccuratePredictions;

        const [showNote, setShowNote] = useState<boolean>(
            Boolean(answer?.hasNote),
        );
        const [showQuote, setShowQuote] = useState<boolean>(
            Boolean(answer?.hasClips),
        );
        const [flagActive, setFlagActive] = useState<boolean>(
            answer?.answerAlert === AnswerAlert.PositiveAlert ||
                answer?.answerAlert === AnswerAlert.NegativeAlert,
        );

        useEffect(() => {
            setShowNote(!!answer?.hasNote);
            setShowQuote(!!answer?.hasClips);
        }, [answer, answer?.soundClipAnswers]);

        useEffect(() => {
            if (firstActiveTag && answer && flagActive) {
                if (
                    firstActiveTag.sentiment === TagSentiment.Neutral ||
                    firstActiveTag.sentiment === TagSentiment.Positive
                ) {
                    answer.answerAlert = AnswerAlert.PositiveAlert;
                } else {
                    answer.answerAlert = AnswerAlert.NegativeAlert;
                }
            }
        }, [firstActiveTag, answer, flagActive]);

        function toggleQuoteField() {
            setShowQuote((prevState) => !prevState);
        }

        function toggleNote() {
            setShowNote((prevState) => !prevState);
        }

        const toggleAnswerAlert = () => {
            if (flagActive === true) {
                setFlagActive(false);
                if (answer) answer.answerAlert = null;
            } else {
                if (firstActiveTag && answer) {
                    setFlagActive(true);
                    if (
                        firstActiveTag.sentiment === TagSentiment.Neutral ||
                        firstActiveTag.sentiment === TagSentiment.Positive
                    ) {
                        answer.answerAlert = AnswerAlert.PositiveAlert;
                    } else {
                        answer.answerAlert = AnswerAlert.NegativeAlert;
                    }
                }
            }
        };

        const bgColor = showNote || showQuote ? "unset" : "unset";

        let showAIExplanationCard = store.showAIExplanation[props.question.id];

        const hasValidDescription = !!sanitizeHtml(props.question.helperText ?? "");

        useEffect(() => {
            if (Array.isArray(activeTags)) {
                props.moduleUIModel?.updateModuleScore(
                    props.question,
                    activeTags,
                );
            } else {
                props.moduleUIModel?.updateModuleScore(
                    props.question,
                    activeTags ? [activeTags] : [],
                );
            }
        }, [props.question, props.moduleUIModel, activeTags]);

        return (
            <Grid
                id={`question-${props.question.id}`}
                item
                xs={12}
                style={{
                    backgroundColor: bgColor,
                    paddingLeft:
                        props.question.parentId || props.leftBorderColor
                            ? "16px"
                            : "0px",
                    borderLeft: props.leftBorderColor
                        ? `2px solid ${props.leftBorderColor}`
                        : "none",
                }}
                container
                direction={"column"}
                justifyContent={"flex-start"}
                alignItems="stretch"
            >
                <Grid
                    item
                    xs={12}
                    container
                    direction={"row"}
                    justifyContent={"flex-start"}
                    alignItems={"center"}
                    wrap={"nowrap"}
                    sx={{
                        marginBottom: props.standalone
                            ? theme.spacing(0.25)
                            : theme.spacing(1),
                        marginTop: props.standalone
                            ? theme.spacing(0.5)
                            : theme.spacing(2),
                        paddingRight: theme.spacing(3.5),
                        paddingLeft: !props.standalone ? theme.spacing(3) : 0,
                    }}
                >
                    <Grid
                        container
                        item
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item>
                            <Typography
                                sx={{
                                    color: "#1F1F1F",
                                    fontSize: props.standalone
                                        ? "12px"
                                        : "14px",
                                    letterSpacing: "0",
                                    fontWeight: "bold",
                                    lineHeight: "20px",
                                }}
                            >
                                {props.question?.questionText}{" "}
                                {props.question.required &&
                                    !answer?.isAnswered && (
                                        <span
                                            style={{
                                                color: theme.palette.error.main,
                                            }}
                                        >
                                            *
                                        </span>
                                    )}
                            </Typography>
                        </Grid>

                        <SmartPredictToggle
                            question={props.question}
                            moduleUIModel={props.moduleUIModel}
                        />
                    </Grid>
                    {props.question.type === QuestionType.QuestionHeading && (
                        <QuoteNoteButtonContainer
                            toggleNote={toggleNote}
                            showNote={showNote}
                            toggleQuote={toggleQuoteField}
                            showQuote={showQuote}
                            toggleAnswerAlert={toggleAnswerAlert}
                            flagActive={flagActive}
                            restrictMedia={
                                !store.authStore.isUserUltra() &&
                                store.authStore.canUserView("Restrict Media")
                            }
                        />
                    )}
                </Grid>
                {!!props.question.rubric && (
                    <Grid
                        item
                        xs={12}
                        container
                        direction={"row"}
                        justifyContent={"flex-start"}
                        alignItems={"center"}
                        wrap={"nowrap"}
                        sx={{
                            paddingLeft: !props.standalone
                                ? theme.spacing(3)
                                : 0,
                        }}
                        paddingTop={theme.spacing(1)}
                        paddingBottom={theme.spacing(1)}
                    >
                        <AcxQuestionRubric urlString={props.question.rubric} />
                    </Grid>
                )}

                {hasValidDescription && (
                    <Grid
                        item
                        container
                        xs={12}
                        sx={{
                            paddingLeft: !props.standalone
                                ? theme.spacing(3)
                                : 0,
                        }}
                    >
                        <AcxQuestionDescription
                            text={props.question.helperText ?? ""}
                            isModalForTablesVariant={props.standalone}
                        />
                    </Grid>
                )}
                {props.question.type !== QuestionType.QuestionHeading && (
                    <Grid
                        item
                        xs={12}
                        direction={"row"}
                        wrap={"nowrap"}
                        container
                        justifyContent={
                            props.standalone ? "flex-start" : "space-between"
                        }
                        alignItems={"center"}
                        sx={{
                            paddingLeft: !props.standalone
                                ? theme.spacing(3)
                                : 0,
                        }}
                    >
                        <Grid
                            item
                            sm={props.standalone ? 12 : 7}
                            md={props.standalone ? 12 : 8}
                            lg={props.standalone ? 12 : 9}
                            xl={props.standalone ? 12 : 10}
                            style={{ marginBottom: "8px" }}
                        >
                            <AnswerSelectorComponent
                                answer={answer}
                                store={store}
                                activeTags={activeTags}
                                question={props.question}
                                standalone={props.standalone}
                                leftBorderColor={props.leftBorderColor}
                                moduleUIModel={props.moduleUIModel}
                                isDisabled={props.isDisabled}
                            />
                        </Grid>
                        {!props.standalone && (
                            <Grid
                                item
                                xs={2}
                                container
                                justifyContent="space-evenly"
                                wrap={"nowrap"}
                                style={{ marginRight: "16px" }}
                            >
                                <QuoteNoteButtonContainer
                                    toggleNote={toggleNote}
                                    showNote={showNote}
                                    toggleQuote={toggleQuoteField}
                                    showQuote={showQuote}
                                    toggleAnswerAlert={toggleAnswerAlert}
                                    flagActive={flagActive}
                                    restrictMedia={
                                        !store.authStore.isUserUltra() &&
                                        store.authStore.canUserView(
                                            "Restrict Media",
                                        )
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
                {!props.standalone && (
                    <QuoteNote
                        question={props.question}
                        answer={answer}
                        showNote={showNote}
                        showQuote={showQuote}
                        noteXs={12}
                        columnDirection={"column-reverse"}
                        isDisabled={props.isDisabled}
                        evaluationModuleId={
                            props.moduleUIModel?.licensedModule
                                .evaluationModuleId
                        }
                    />
                )}
                {isSmartPredicted && showAIExplanationCard && activeTags && (
                    <AIExplanationCards
                        activeTags={
                            !Array.isArray(activeTags)
                                ? [activeTags]
                                : activeTags
                        }
                        predictedTags={answer.predictedTags}
                        questionId={props.question.id}
                        answer={answer}
                    />
                )}
            </Grid>
        );
    },
);

const AnswerSelectorComponent = observer(
    (
        props: OwnProps & {
            children?: React.ReactNode;
            store: EvalStore;
            answer: Answer | undefined;
            activeTags: Tag | Tag[] | undefined;
        },
    ) => {
        const messageStore = props.store.rootStore.getStore(MessageStore);
        const evalModuleId =
            props.moduleUIModel?.licensedModule.evaluationModuleId;

        function onFillInAnswerSelect(fillInAnswer: string) {
            props.store.currentEval?.setFillInAnswerForQuestion(
                props.question,
                fillInAnswer,
                props.answer,
                evalModuleId,
                // props.store.authStore._user.profile.email,
            );
        }

        if (
            props.question.answerType.variation === AnswerTypeVariations.Single
        ) {
            function onSingleAnswerSelect(arg?: Tag) {
                props.store.currentEval?.setAnswerForQuestion(
                    props.question,
                    arg ? [arg] : [],
                    props.answer,
                    evalModuleId,
                    props.store.enableAutoBindClips,
                    messageStore,
                );
            }

            return (
                <>
                    <AcxSelectSingle
                        options={
                            props.moduleUIModel?.getTagsFormattedForTagResponse(
                                props.question.id,
                            ) ?? []
                        }
                        containerHeight={"auto"}
                        customStyle={selectCustomStyle}
                        menuPlacement={"auto"}
                        menuPortalTarget={document.body}
                        id={`tag-response-${props.question.id}`}
                        fullWidth
                        colorField={
                            props.moduleUIModel?.moduleTypeName ===
                            "Interaction"
                                ? undefined
                                : "sentimentColor"
                        }
                        defaultValue={props.activeTags as any}
                        valueField={"id"}
                        labelField={"value"}
                        isClearable
                        onChange={onSingleAnswerSelect}
                        isDisabled={props.isDisabled}
                    />
                </>
            );
        } else if (
            props.question.answerType.answerTypeName ===
            AnswerTypeNames.DateResponse
        ) {
            const currentDate = props.answer?.fillInAnswerValue
                ? moment(props.answer.fillInAnswerValue)
                : undefined;

            return (
                <AcxCalendarPopup
                    id={`fill-in-response-${props.question.id}`}
                    onSelect={(date) =>
                        onFillInAnswerSelect(date.format("YYYY-MM-DD"))
                    }
                    currentDate={currentDate}
                    disabled={props.isDisabled}
                />
            );
        } else if (
            props.question.answerType.answerTypeName ===
            AnswerTypeNames.TextResponse
        ) {
            const value = props.answer?.fillInAnswerValue
                ? props.answer.fillInAnswerValue
                : undefined;

            // eslint-disable-next-line react-hooks/exhaustive-deps
            const debounceOnChange = React.useCallback(
                _.debounce((fillInValue) => {
                    onFillInAnswerSelect(fillInValue);
                }, 500),
                [],
            );

            return (
                <AcxMainTextField
                    id={`fill-in-response-${props.question.id}`}
                    fullWidth
                    onChange={(evt) => debounceOnChange(evt.target.value)}
                    multiline
                    rows={3}
                    value={value}
                    maxLength={props.question.maxLength}
                    isDisabled={props.isDisabled}
                />
            );
        } else {
            function onAnswerSelect(arg?: Tag[]) {
                props.store.currentEval?.setAnswerForQuestion(
                    props.question,
                    arg ?? [],
                    props.answer,
                    evalModuleId,
                    props.store.enableAutoBindClips,
                    messageStore,
                );
            }

            return (
                <>
                    <AcxSelectMulti
                        options={
                            props.moduleUIModel?.getTagsFormattedForTagResponse(
                                props.question.id,
                            ) ?? []
                        }
                        inputLabel={"Add tag(s)"}
                        containerHeight={"auto"}
                        customStyle={selectCustomStyle}
                        menuPlacement={"auto"}
                        menuPortalTarget={document.body}
                        id={`tag-response-${props.question.id}`}
                        fullWidth
                        colorField={"sentimentColor"}
                        defaultValue={props.activeTags as any}
                        valueField={"id"}
                        labelField={"value"}
                        isClearable
                        onChange={onAnswerSelect}
                        isDisabled={props.isDisabled}
                    />
                </>
            );
        }
    },
);

export default TagOrFillInResponseAnswer;
